/**
 * Utility functions for normalizing tutor data
 */

/**
 * Normalizes tutor subjects to always be an array
 * Handles cases where subjects might be stored as a JSON string
 */
export const normalizeTutorSubjects = (tutor: any) => {
  let subjects: string[] = [];
  
  if (Array.isArray(tutor.subjects)) {
    subjects = tutor.subjects;
  } else if (typeof tutor.subjects === 'string' && tutor.subjects.trim()) {
    try {
      const parsed = JSON.parse(tutor.subjects);
      subjects = Array.isArray(parsed) ? parsed : [];
    } catch {
      // If parsing fails, treat as empty array
      subjects = [];
    }
  }
  
  return { ...tutor, subjects };
};

/**
 * Sorts tutors by evaluation score in descending order
 * Tutors with higher evaluation (10) appear first
 * Tutors with no evaluation appear last
 */
export const sortTutorsByEvaluation = (tutors: any[]): any[] => {
  return [...tutors].sort((a, b) => {
    const evalA = a.evaluation ?? -1;
    const evalB = b.evaluation ?? -1;
    return evalB - evalA;
  });
};

/**
 * Normalizes an array of tutors
 */
export const normalizeTutorsList = (tutors: any[]): any[] => {
  return tutors.map(normalizeTutorSubjects);
};

/**
 * Safely gets subjects as an array from a tutor object
 */
export const getTutorSubjects = (tutor: any): string[] => {
  if (Array.isArray(tutor.subjects)) {
    return tutor.subjects;
  }
  if (typeof tutor.subjects === 'string' && tutor.subjects.trim()) {
    try {
      const parsed = JSON.parse(tutor.subjects);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Capitalizes the first letter of each word in a subject name
 */
export const capitalizeSubject = (subject: string): string => {
  if (!subject) return '';
  return subject
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};
